import { Component } from '@theme/component';

export class LookbookComponent extends Component {
  requiredRefs = ['track'];

  #looping = false;
  #settleTimer = null;
  #resizeObserver = null;
  #index = 0;

  connectedCallback() {
    super.connectedCallback();
    const { track } = this.refs;
    if (!track) return;

    this.#setupLoop();
    track.addEventListener('scroll', this.#onScroll, { passive: true });
    this.#resizeObserver = new ResizeObserver(this.#onResize);
    this.#resizeObserver.observe(track);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    const { track } = this.refs;
    if (track) track.removeEventListener('scroll', this.#onScroll);
    this.#resizeObserver?.disconnect();
    this.#resizeObserver = null;
    if (this.#settleTimer) {
      clearTimeout(this.#settleTimer);
      this.#settleTimer = null;
    }
  }

  // Clone the full slide set before and after the originals so scrolling never
  // hits a hard edge. The originals stay in the middle; we pin scroll there and
  // silently reset back into that band whenever a scroll settles on a clone.
  // Each clone is pixel-identical to the slide it mirrors, so the wrap is invisible.
  #setupLoop() {
    const { track } = this.refs;
    const originals = Array.from(track.children);
    if (originals.length < 2) return;

    const before = document.createDocumentFragment();
    const after = document.createDocumentFragment();
    for (const el of originals) {
      after.appendChild(this.#makeClone(el));
      before.appendChild(this.#makeClone(el));
    }
    track.appendChild(after);
    track.insertBefore(before, track.firstChild);

    this.#looping = true;
    this.#pinToMiddle();
  }

  #makeClone(el) {
    const clone = el.cloneNode(true);
    clone.setAttribute('aria-hidden', 'true');
    clone.setAttribute('inert', '');
    clone.dataset.lookbookClone = '';
    return clone;
  }

  // Width of one copy of the slide set (the track holds three identical copies).
  get #setWidth() {
    const { track } = this.refs;
    return track ? track.scrollWidth / 3 : 0;
  }

  // Snap offsets for just the middle (originals) copy — the band scroll is pinned
  // within. The full #snapOffsets list holds three equal copies, so the middle
  // copy is its middle third.
  get #midOffsets() {
    const all = this.#snapOffsets;
    if (all.length < 3) return all;
    const per = Math.round(all.length / 3);
    return all.slice(per, per * 2);
  }

  // Index of the middle-copy snap point nearest the current scroll position.
  #nearestMidIndex() {
    const { track } = this.refs;
    const mids = this.#midOffsets;
    if (!track || !mids.length) return 0;
    let best = 0;
    let bestDist = Infinity;
    mids.forEach((x, i) => {
      const dist = Math.abs(x - track.scrollLeft);
      if (dist < bestDist) {
        bestDist = dist;
        best = i;
      }
    });
    return best;
  }

  // Snap points depend on the breakpoint: every slide on mobile, page-start
  // slides only on desktop. Read it from the resolved CSS so JS stays in sync.
  // Hidden slides (the desktop/mobile-only variants) report offsetWidth 0.
  // Read from the live DOM so cloned slides count as valid snap targets.
  get #snapOffsets() {
    const { track } = this.refs;
    if (!track) return [];
    return Array.from(track.children)
      .filter((slide) => {
        if (!slide.offsetWidth) return false;
        const align = getComputedStyle(slide).scrollSnapAlign;
        return align && align !== 'none' && align !== 'none none';
      })
      .map((slide) => slide.offsetLeft)
      .sort((a, b) => a - b);
  }

  scrollPrev = (event) => {
    event?.preventDefault();
    const { track } = this.refs;
    if (!track) return;
    const prev = this.#snapOffsets.filter((x) => x < track.scrollLeft - 1);
    if (prev.length) {
      track.scrollTo({ left: Math.max(...prev), behavior: 'smooth' });
    } else {
      track.scrollTo({ left: 0, behavior: 'smooth' });
    }
  };

  scrollNext = (event) => {
    event?.preventDefault();
    const { track } = this.refs;
    if (!track) return;
    const next = this.#snapOffsets.filter((x) => x > track.scrollLeft + 1);
    if (next.length) {
      track.scrollTo({ left: Math.min(...next), behavior: 'smooth' });
    } else {
      track.scrollTo({ left: track.scrollWidth, behavior: 'smooth' });
    }
  };

  #onScroll = () => {
    if (!this.#looping) return;
    clearTimeout(this.#settleTimer);
    this.#settleTimer = setTimeout(this.#normalize, 120);
  };

  // Once scrolling has settled, slide the position back into the middle copy.
  // The clone we landed on looks identical to the matching middle slide, so this
  // instant (non-smooth) jump is seamless and keeps a full set of buffer on each side.
  #normalize = () => {
    const { track } = this.refs;
    const one = this.#setWidth;
    if (!track || !one) return;

    let x = track.scrollLeft;
    if (x >= one * 2) x -= one;
    else if (x < one) x += one;

    if (Math.abs(x - track.scrollLeft) > 0.5) this.#jumpTo(x);

    // Remember where we settled so a resize can return to this slide.
    this.#index = this.#nearestMidIndex();
  };

  #pinToMiddle() {
    this.#jumpTo(this.#setWidth);
  }

  // Instant scroll (bypasses the CSS smooth-scroll) so repositioning is invisible.
  #jumpTo(left) {
    const { track } = this.refs;
    if (!track) return;
    const prev = track.style.scrollBehavior;
    track.style.scrollBehavior = 'auto';
    track.scrollLeft = left;
    track.style.scrollBehavior = prev;
  }

  // On breakpoint/size changes the per-set width changes, so the old scroll
  // position no longer lines up. Re-pin into the middle copy, but to the slide we
  // were already on rather than back to the start. If the breakpoint changed the
  // snap layout, the index is clamped to what's available.
  #onResize = () => {
    if (!this.#looping) return;
    const mids = this.#midOffsets;
    if (!mids.length) {
      this.#pinToMiddle();
      return;
    }
    const i = Math.min(Math.max(this.#index, 0), mids.length - 1);
    this.#jumpTo(mids[i]);
  };
}

customElements.define('lookbook-component', LookbookComponent);
